import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-all duration-150 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40 focus-visible:ring-offset-2 focus-visible:ring-offset-raios-bg active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 min-h-[44px] sm:min-h-0 cursor-pointer',
  {
    variants: {
      variant: {
        default:
          'bg-raios-elevated text-raios-text hover:bg-raios-elevated/80',
        brand:
          'bg-raios-accent text-white hover:bg-raios-accent-hover focus-visible:shadow-glow-blue',
        destructive:
          'bg-raios-danger text-white hover:bg-raios-danger/90',
        outline:
          'border border-raios-border bg-transparent text-raios-text hover:bg-raios-elevated',
        ghost:
          'bg-transparent text-raios-text hover:bg-raios-elevated',
        link:
          'text-raios-accent-text underline-offset-4 hover:underline bg-transparent',
      },
      size: {
        sm: 'h-9 px-3 text-xs',
        default: 'h-10 px-4 text-sm',
        lg: 'h-11 px-8 text-sm',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  /** Required for icon-only buttons (size="icon") */
  'aria-label'?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    if (process.env.NODE_ENV !== 'production' && size === 'icon' && !props['aria-label']) {
      console.warn('Button: icon-only buttons must have an aria-label for accessibility.');
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            <span className="sr-only">Carregando</span>
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
export type { ButtonProps };
